import { selectWarehouse, updateGateState, updateSelectedGateGateway } from 'containers/Warehouses/warehousesSlice'
import useOpeningClosingActions from '../core/hooks/useOpeningClosingActions'
import useWarehouseAlert from 'containers/Warehouses/hooks/useWarehouseAlert'
import CheckingLocationPopup from 'components/popups/CheckingLocationPopup'
import CodePreview from 'containers/Warehouse/MagAccess/popups/CodePreview'
import useRentalStatus from 'containers/Warehouse/hooks/useRentalStatus'
import RentalStatusOption from 'containers/Warehouse/RentalStatusOption'
import { getGateRemainingTime } from 'containers/Warehouses/gateMotion'
import { GATEWAY_STATUS, GATE_STATUS, STATUS } from 'utils/constants'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { ReactComponent as MagIcon } from 'assets/magDetails.svg'
import { ReactComponent as HelpIcon } from 'assets/helpBig.svg'
import { ReactComponent as GateIcon } from 'assets/gate.svg'
import { ReactComponent as DoorIcon } from 'assets/door.svg'
import ReportProblemModal from '../core/ReportProblemModal'
import BigCircleBtn from 'components/buttons/BigCircleBtn'
import { useReduxDispatch, useReduxSelector } from 'store'
import { selectConfig, selectLightTheme } from 'appSlice'
import KeyCabinetBadge from 'containers/KeyCabinet/Badge'
import { getGateStatusLabel } from '../core/gateLabel'
import SelectGateModal from '../core/SelectGateModal'
import CircleBtn from 'components/buttons/CircleBtn'
import { useEffect, useRef, useState } from 'react'
import useTranslation from 'hooks/useTranslation'
import useWarehouses from 'hooks/useWarehouses'
import usePopupState from 'hooks/usePopupState'
import Player from 'components/Player'
import Gateway from '../core/Gateway'
import Gate from '../core/Gate'
import '../style.scss'

const type = process.env.REACT_APP_TYPE

const GateGateway = () => {
  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()
  const config = useReduxSelector(selectConfig)
  const [isPreview, showPreview] = useState(false)
  const lightTheme = useReduxSelector(selectLightTheme)
  const warehouseData = useReduxSelector(state => selectWarehouse(state, id))
  const { placeId, actions, placeAddress, placeShortAddress, placeGps, gateCamera, hasCodes, hasAccess, isShared, contractId, objectId } = warehouseData
  const { alert } = useWarehouseAlert(warehouseData, { withAlarm: false })
  const { status, place, gateId, gatewayId, isSending, moreActions, stoppedAction, action } = useOpeningClosingActions(placeId, actions)
  const { popup, openPopup } = usePopupState<{ type: 'checkingGateLocation' | 'selectGateOrGateway', props: { actionType: warehouse.OpeningClosingGateGatewayActionType } } | { type: 'reportProblem' | 'codePreview' }>()
  const search = useRef(window.location.search)
  const rentalStatus = useRentalStatus(id, 'gate')

  useWarehouses()
  const dispatch = useReduxDispatch()

  /**
   * Temporary solution
   */
  const gateMotion = place?.gates?.[gateId]
  useEffect(() => {
    const time = actions?.gates?.[gateId]?.open?.time
    if (gateMotion?.state === GATE_STATUS.opening && gateId && time) {
      const remaining = gateMotion.since !== undefined ? getGateRemainingTime(gateMotion) : time * 1000
      const timeout = setTimeout(() => {
        dispatch(updateGateState({ id: gateId, placeId, state: GATE_STATUS.opened }))
      }, remaining)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [actions?.gates, gateId, dispatch, placeId, gateMotion])

  const { push, replace, goBack } = useHistory()
  const { t } = useTranslation('entrance')

  const isGateway = pathname.includes('gateway')
  const thereAreAccessCodes = hasCodes && !isShared
  const gateActionKeys = Object.keys(actions?.gates || {})
  const gatewayActionKeys = Object.keys(actions?.gateways || {})
  const hasBadge = hasAccess && actions?.key_cabinet?.grant?.canOpen

  return (
    <>
      {popup?.type === 'checkingGateLocation' && <CheckingLocationPopup
        targetPosition={(Array.isArray(placeGps) && placeGps.length === 2) ? {
          lat: Number(placeGps[0]),
          lng: Number(placeGps[1])
        } : undefined}
        callback={(success) => {
          if (success) {
            const objectType = isGateway ? 'gateways' : 'gates'
            const moreThanOneObject = Object.keys(place?.[objectType] || {}).length > 1
            if (popup.props?.actionType === 'open' && moreThanOneObject) {
              openPopup({ type: 'selectGateOrGateway', props: { actionType: popup.props?.actionType } }, true)

            } else {
              action(objectType, popup.props?.actionType)
              goBack()
            }
          } else {
            goBack()
          }
        }}
      />}
      {popup?.type === 'selectGateOrGateway' && <SelectGateModal
        isGate={!isGateway}
        options={Object.values(place?.[isGateway ? 'gateways' : 'gates'] || {})}
        callback={objectId => {
          goBack()
          dispatch(updateSelectedGateGateway({ placeId, object: isGateway ? 'gateway' : 'gate', objectId }))
          action(isGateway ? 'gateways' : 'gates', objectId, popup.props?.actionType)
        }}
      />}
      {popup?.type === 'reportProblem' && <ReportProblemModal
        warehouseId={objectId}
        contractId={contractId}
      />}
      {popup?.type === 'codePreview' && <CodePreview
        contractId={contractId}
      />}

      <div className='entrance warehouse'>
        {hasBadge && <KeyCabinetBadge
          id={id}
          purpose={actions?.key_cabinet?.grant?.purpose}
        />}
        {gateCamera && type === 'mm' && <div
          className='warehouse__previewBtn'
          onClick={() => {
            showPreview(prev => !prev)
          }}>
          <i className='icon icon--monitoring s32' />
        </div>}
        <div className={`entrance__main center${lightTheme ? '' : ' --shadow'}`}>
          {isPreview && gateCamera
            ? <Player
              camera={gateCamera?.hqCam || gateCamera?.defaultCam}
            />
            : isGateway
              ? <Gateway status={status.gateway} alert={alert} />
              : <Gate
                status={status.gate}
                motion={gateMotion}
                onReportProblem={(type === 'rze01' || type === 'chr01')
                  ? () => openPopup({ type: 'reportProblem' })
                  : () => push(`/help?warehouseId=${id}`)}
                alert={alert}
              />}
          <div
            className='entrance__main__label --bottom'
            onClick={() => {
              window.open(`https://www.google.com/maps/search/?api=1&query=${placeAddress}`)
            }}>
            <i className='icon icon--location-500' />
            <span>{placeShortAddress}</span>
          </div>
        </div>

        <div className='entrance__menu menu__main'>
          <div className='menu__status'>
            <div className='menu__status--margin' />
            <div className='status__options'>
              {isGateway
                ? <div className='status__option'>
                  <i className={`icon icon--padlock-${(status.gateway === GATEWAY_STATUS.opened || status.gateway === GATEWAY_STATUS.unlocked) ? 'open' : 'closed'}`} />
                  <span>
                    {t(status.gateway === GATEWAY_STATUS.opened
                      ? 'wicketOpen'
                      : status.gateway === GATEWAY_STATUS.unlocked
                        ? 'wicketLatchReleased'
                        : 'wicketClosed')}
                  </span>
                </div>
                : (status.gate !== GATE_STATUS.malfunction && rentalStatus)
                  ? <RentalStatusOption status={rentalStatus} />
                  : <div className='status__option'>
                    <i className={`icon icon--padlock-${status.gate === GATE_STATUS.opened ? 'open' : 'closed'}`} />
                    <span>
                      {t(getGateStatusLabel(status.gate))}
                    </span>
                  </div>}
            </div>
          </div>

          <div className='menu__buttons --top'>
            {isGateway
              ? <CircleBtn
                disabled={gateActionKeys.length < 1}
                text={t('openGate')}
                Icon={GateIcon}
                action={() => {
                  replace(pathname.replace('gateway', 'gate'))
                }}
              />
              : (type === 'rze01' || type === 'chr01')
                ? thereAreAccessCodes
                  ? <CircleBtn
                    disabled={!hasAccess}
                    text={t('accessCode')}
                    Icon={'pin'}
                    action={() => {
                      openPopup({ type: 'codePreview' })
                    }} />
                  : <CircleBtn
                    text={t('reportProblem')}
                    Icon='problem'
                    action={() => {
                      openPopup({ type: 'reportProblem' })
                    }} />
                : <CircleBtn
                  disabled={gatewayActionKeys.length < 1}
                  text={t('openGateway')}
                  Icon={DoorIcon}
                  action={() => {
                    replace(pathname.replace('gate', 'gateway'))
                  }}
                />}

            <div className='buttons__col center'>
              {isGateway
                ? <BigCircleBtn
                  disabled={isSending || gatewayActionKeys.length < 1}
                  text={t(isSending ? 'loading' : status.gateway === GATEWAY_STATUS.closed ? 'open' : 'enterThroughGate')}
                  status={status.gateway === GATEWAY_STATUS.opened ? STATUS.succeed : STATUS.idle}
                  action={() => {
                    if (/* status.gateway === GATEWAY_STATUS.closed &&  */actions?.gateways?.[gatewayActionKeys[0]]?.open) {
                      if (config?.warehouseLocationCheckEnabled) {
                        openPopup({ type: 'checkingGateLocation', props: { actionType: 'open' } })
                      } else if (gatewayActionKeys.length > 1) {
                        openPopup({ type: 'selectGateOrGateway', props: { actionType: 'open' } })
                      } else {
                        action('gateways', gatewayId)
                      }
                    }
                  }}
                />
                : <BigCircleBtn
                  disabled={isSending || gateActionKeys.length < 1}
                  text={t(isSending
                    ? 'loading'
                    : (type === 'rze01' || type === 'chr01')
                      ? 'open'
                      : status.gate === GATE_STATUS.opening
                        ? moreActions ? 'stop' : 'opening'
                        : status.gate === GATE_STATUS.closing
                          ? moreActions ? 'stop' : 'closing'
                          : status.gate === GATE_STATUS.opened
                            ? moreActions ? 'close' : 'driveThroughGate'
                            : stoppedAction === 'close' ? 'close' : 'open')}
                  status={status.gate?.includes('ing') ? STATUS.pending : STATUS.idle}
                  action={() => {
                    if ((type === 'rze01' || type === 'chr01') && actions?.gates) {
                      if (config?.warehouseLocationCheckEnabled) {
                        openPopup({ type: 'checkingGateLocation', props: { actionType: 'open' } })
                      } else if (gateActionKeys.length > 1) {
                        openPopup({ type: 'selectGateOrGateway', props: { actionType: 'open' } })
                      } else {
                        action('gates', gateId, 'open')
                      }

                    } else if ((
                      stoppedAction === 'open' ||
                      status.gate === GATE_STATUS.closed ||
                      (!stoppedAction && status.gate === GATE_STATUS.stopped) ||
                      status.gate === GATE_STATUS.malfunction
                    ) && actions?.gates?.[gateActionKeys[0]]?.open) {
                      if (config?.warehouseLocationCheckEnabled) {
                        openPopup({ type: 'checkingGateLocation', props: { actionType: 'open' } })
                      } else if (gateActionKeys.length > 1) {
                        openPopup({ type: 'selectGateOrGateway', props: { actionType: 'open' } })
                      } else {
                        action('gates', gateId, 'open')
                      }

                    } else if (
                      moreActions &&
                      (stoppedAction === 'close' || status.gate === GATE_STATUS.opened) &&
                      actions?.gates?.[gateId]?.close) {
                      if (config?.warehouseLocationCheckEnabled) {
                        openPopup({ type: 'checkingGateLocation', props: { actionType: 'close' } })
                      } else {
                        action('gates', gateId, 'close')
                      }

                    } else if (
                      moreActions &&
                      (status.gate === GATE_STATUS.closing || status.gate === GATE_STATUS.opening) &&
                      actions?.gates?.[gateId]?.stop) {
                      if (config?.warehouseLocationCheckEnabled) {
                        openPopup({ type: 'checkingGateLocation', props: { actionType: 'stop' } })
                      } else {
                        action('gates', gateId, 'stop')
                      }
                    }
                  }}
                />}

              <div style={(type === 'rze01' || type === 'chr01') && !thereAreAccessCodes ? { visibility: 'hidden' } : undefined}>
                {(type === 'rze01' || type === 'chr01')
                  ? <CircleBtn
                    text={t('reportProblem')}
                    Icon='problem'
                    action={() => {
                      openPopup({ type: 'reportProblem' })
                    }} />
                  : <CircleBtn
                    text={t('help')}
                    Icon={HelpIcon}
                    action={() => {
                      push(`/help?warehouseId=${id}`)
                    }} />}
              </div>
            </div>

            <CircleBtn
              text={t(`manageMag.${type}`)}
              Icon={MagIcon}
              action={() => {
                if (search.current.includes('from=mag')) goBack()
                else replace(`/warehouses/${id}`)
              }} />
          </div>
        </div>
      </div>
    </>
  )
}

export default GateGateway